import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, QrCode, Upload, X } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  deletePayoutQr,
  listPayoutMethods,
  listPayoutQrs,
  uploadPayoutQr,
  type PayoutMethodOption,
  type PayoutQr,
} from '../../lib/api';

/**
 * Saved payout QR codes — one per payout method the tenant offers.
 *
 * The method list comes from the admin-managed registry (Fees & Tax), so
 * "GCash" / "QRPH" / a bank appear only because an admin configured them. When
 * the member requests a cash-out, the QR saved for the chosen method is
 * SNAPSHOTTED onto that request, so replacing it later never rewrites the
 * picture an admin already reviewed.
 */
export function PayoutQrTab() {
  const qc = useQueryClient();

  const methodsQ = useQuery({ queryKey: ['payout-methods'], queryFn: listPayoutMethods });
  const qrsQ = useQuery({ queryKey: ['payout-qrs'], queryFn: listPayoutQrs });

  const methods: PayoutMethodOption[] = methodsQ.data ?? [];
  const saved = new Map((qrsQ.data ?? []).map((q) => [q.methodCode, q]));

  if (methodsQ.isLoading || qrsQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Loading…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-4 w-4" />
          Payout QR codes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Upload the QR code your payout should be sent to, for each method you use. When you
          request a cash-out, the QR saved for that method is attached to the request so the
          finance team can scan it. PNG or JPG, up to 5 MB.
        </p>

        {methods.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No payout methods are available yet. Once an admin sets them up, you can save a QR
            code for each one here.
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {methods.map((m) => (
              <QrSlot
                key={m.code}
                method={m}
                existing={saved.get(m.code) ?? null}
                onChanged={() => qc.invalidateQueries({ queryKey: ['payout-qrs'] })}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QrSlot({
  method,
  existing,
  onChanged,
}: {
  method: PayoutMethodOption;
  existing: PayoutQr | null;
  onChanged: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Local preview so the tile updates the instant a file is picked, before the
  // upload round-trip returns (same two-step the KYC tab uses).
  const [preview, setPreview] = useState<string | null>(null);
  const [label, setLabel] = useState(existing?.label ?? '');
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    meta: { successMessage: 'Payout QR saved.' },
    mutationFn: (file: File) =>
      uploadPayoutQr({ file, methodCode: method.code, label: label.trim() || undefined }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error ?? 'upload_failed');
      setPreview(null);
    },
  });

  const remove = useMutation({
    meta: { successMessage: 'Payout QR removed.' },
    mutationFn: () => deletePayoutQr(existing!.id),
    onSuccess: () => {
      setPreview(null);
      onChanged();
    },
  });

  function onPick(file: File | null) {
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    upload.mutate(file);
  }

  const shownSrc = preview ?? existing?.qrUrl ?? null;
  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium">{method.label}</label>
      <div className="relative aspect-square overflow-hidden rounded-md border-2 border-dashed border-muted-foreground/30">
        {shownSrc ? (
          <>
            <img src={shownSrc} alt={`${method.label} payout QR`} className="h-full w-full object-contain" />
            {busy && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
            {existing && !busy && (
              <button
                type="button"
                onClick={() => remove.mutate()}
                className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground hover:bg-background"
                aria-label={`Remove ${method.label} payout QR`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-full w-full flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Upload className="h-5 w-5" />
            Tap to upload
          </button>
        )}
      </div>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (optional), e.g. GCash personal"
        className="h-8 text-xs"
      />
      {shownSrc && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          Replace
        </Button>
      )}
      {error && <p className="text-[11px] text-destructive">{error.replace(/_/g, ' ')}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files?.[0] ?? null);
          // Allow re-picking the same file after a failed upload.
          e.target.value = '';
        }}
      />
    </div>
  );
}
